import { useSearch, useNavigate, useLocation } from "@tanstack/react-router";

export type Locale = "en" | "fr";
export const DEFAULT_LOCALE: Locale = "en";
export const LOCALES: Locale[] = ["en", "fr"];

export interface LocalizedString {
  en?: string;
  fr?: string;
  _type?: string;
}

export function pickLocalized<T extends Record<string, any> | undefined>(
  field: T,
  locale: Locale,
): string {
  if (!field) return "";
  if (typeof field === "string") return field;
  return (field[locale] as string) || (field[DEFAULT_LOCALE] as string) || "";
}

export function pickLocalizedRaw<T = any>(field: any, locale: Locale): T | undefined {
  if (!field) return undefined;
  return (field[locale] as T) ?? (field[DEFAULT_LOCALE] as T);
}

export function useLocale(): Locale {
  // search is route-typed; cast to any since lang isn't in route schemas yet
  const search = useSearch({ strict: false }) as { lang?: string };
  const lang = search?.lang;
  return lang === "fr" ? "fr" : "en";
}

export function useLocaleSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  return (next: Locale) => {
    navigate({
      to: location.pathname,
      search: (prev: any) => ({ ...prev, lang: next === DEFAULT_LOCALE ? undefined : next }),
    });
  };
}

export const t = {
  nav: {
    home: { en: "Home", fr: "Accueil" },
    news: { en: "News", fr: "Actualités" },
    faq: { en: "FAQ", fr: "FAQ" },
    blog: { en: "Blog", fr: "Blog" },
    contact: { en: "Contact", fr: "Contact" },
  },
  common: {
    loading: { en: "Loading…", fr: "Chargement…" },
    readMore: { en: "Read more", fr: "Lire la suite" },
    backToBlog: { en: "Back to blog", fr: "Retour au blog" },
    backToNews: { en: "Back to news", fr: "Retour aux actualités" },
    trending: { en: "Trending", fr: "Tendances" },
    latest: { en: "Latest articles", fr: "Derniers articles" },
    notFound: { en: "Not found", fr: "Introuvable" },
  },
  news: {
    kinds: {
      announcement: { en: "Announcement", fr: "Annonce" },
      release: { en: "Release", fr: "Nouvelle version" },
      update: { en: "Update", fr: "Mise à jour" },
    },
  },
  cookies: {
    manage: { en: "Cookies", fr: "Cookies" },
  },
  contact: {
    title: { en: "Contact us", fr: "Contactez-nous" },
    name: { en: "Name", fr: "Nom" },
    email: { en: "Email", fr: "Email" },
    subject: { en: "Subject", fr: "Sujet" },
    message: { en: "Message", fr: "Message" },
    send: { en: "Send message", fr: "Envoyer le message" },
    sending: { en: "Sending…", fr: "Envoi…" },
    success: {
      en: "Thanks! We'll get back to you soon.",
      fr: "Merci ! Nous vous répondrons bientôt.",
    },
    error: {
      en: "Something went wrong. Please try again.",
      fr: "Une erreur s'est produite. Réessayez.",
    },
  },
} as const;

export function tr(entry: { en: string; fr: string }, locale: Locale): string {
  return entry[locale];
}

/** Post date formatting: "Aug 25, 2026" in English, "25/08/2026" in French. */
export function formatDate(iso: string, locale: Locale): string {
  const d = new Date(iso);
  if (locale === "fr") {
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getUTCFullYear()}`;
  }
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}
